import { Gender, GenderType } from "@garther/interfaces";
import { Button, Input, Select, Separator } from "@garther/ui/components";
import { Person } from "@mui/icons-material";
import { useForm } from "@tanstack/react-form";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { profileApiClient } from "@/libs/api";
import { firebaseService } from "@/libs/firebase";
import { useAuthStore } from "@/stores/auth.store";
import { useCreditStore } from "@/stores/credit.store";

interface ProfileCreationFormProps {
  onComplete: () => void;
}

interface FormData {
  name: string;
  phone: string;
  dateOfBirth: string;
  gender: GenderType;
  referrerCode: string;
}

export default function ProfileCreationForm({ onComplete }: ProfileCreationFormProps) {
  const { t } = useTranslation();
  const { curUser, loadProfile } = useAuthStore();
  const { loadUserCredits } = useCreditStore();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [showCreditsPopup, setShowCreditsPopup] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      name: curUser?.fbUser?.displayName || "",
      phone: "",
      dateOfBirth: "",
      gender: Gender.PREFER_NOT_TO_SAY,
      referrerCode: ""
    },
    onSubmit: async ({ value }) => {
      if (!curUser?.fbUser?.uid) {
        setError(t("profile.creation.errors.userNotAuthenticated"));
        return;
      }

      setError(null);

      try {
        // Create profile using the authenticated endpoint
        // This will automatically initialize user credits (3 free credits) on the backend
        await profileApiClient.createMyProfile({
          avatar: null,
          name: value.name.trim(),
          email: curUser.fbUser.email || "",
          phone: value.phone,
          gender: value.gender,
          dateOfBirth: value.dateOfBirth,
          referrerCode: value.referrerCode
        });

        // Load user credits to update the store (credits are now initialized on the backend)
        await Promise.all([loadProfile(), loadUserCredits(curUser.fbUser.uid)]);

        // Show credits popup
        setShowCreditsPopup(true);

        // Auto-hide popup after 10 seconds and complete the flow
        setTimeout(() => {
          setShowCreditsPopup(false);
          onComplete();
        }, 10000);
      } catch (err) {
        console.error("Error creating profile:", err);
        setError(t("profile.creation.errors.creationFailed"));
      }
    }
  });

  const handleSignInAnotherAccount = async () => {
    try {
      await firebaseService.signOut();
      router.navigate({ to: "/" });
    } catch (error) {
      console.error("Error signing out:", error);
      setError(t("profile.creation.errors.creationFailed"));
    }
  };

  return (
    <div className="min-h-screen e-center p-0 sm:p-2 md:p-4 bg-background-secondary sm:bg-background-primary">
      <div className="max-w-md w-full">
        {/* Profile Creation Form */}
        <div className="bg-background-secondary rounded-sm border-0 sm:border border-border-dark p-4">
          <div className="text-center mb-4">
            <div className="w-14 h-14 rounded-full mx-auto mb-2 e-center border border-text-secondary">
              <Person className="text-text-secondary" fontSize="large" />
            </div>
            <h1 className="text-responsive-lg font-normal text-text-primary mb-0">
              {t("profile.creation.title")}
            </h1>
            <p className="text-text-secondary text-responsive-base">{t("profile.creation.subtitle")}</p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-3 sm:space-y-4"
          >
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
            )}

            {/* Name Field */}
            <form.Field
              name="name"
              validators={{
                onChange: ({ value }) =>
                  !value?.trim() ? t("profile.creation.errors.nameRequired") : undefined
              }}
            >
              {(field) => (
                <Input
                  id="name"
                  label={t("profile.creation.name")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t("profile.creation.namePlaceholder")}
                  required
                  disabled={form.state.isSubmitting}
                  error={field.state.meta.errors[0] || undefined}
                />
              )}
            </form.Field>

            {/* Phone Field */}
            <form.Field name="phone">
              {(field) => (
                <Input
                  id="phone"
                  label={t("profile.creation.phone")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t("profile.creation.phonePlaceholder")}
                  disabled={form.state.isSubmitting}
                  error={field.state.meta.errors[0] || undefined}
                />
              )}
            </form.Field>

            {/* Date of Birth Field */}
            <form.Field
              name="dateOfBirth"
              validators={{
                onChange: ({ value }) =>
                  !value ? t("profile.creation.errors.dateOfBirthRequired") : undefined
              }}
            >
              {(field) => (
                <Input
                  id="dateOfBirth"
                  type="date"
                  label={t("profile.creation.dateOfBirth")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t("profile.creation.dateOfBirthPlaceholder")}
                  required
                  disabled={form.state.isSubmitting}
                  error={field.state.meta.errors[0] || undefined}
                />
              )}
            </form.Field>

            {/* Gender Field */}
            <form.Field
              name="gender"
              validators={{
                onChange: ({ value }) => (!value ? t("profile.creation.errors.genderRequired") : undefined)
              }}
            >
              {(field) => (
                <Select
                  id="gender"
                  required
                  label={t("profile.creation.gender")}
                  placeholder={t("profile.creation.genderOptions.select")}
                  value={field.state.value}
                  options={Object.values(Gender).map((gender) => ({
                    value: gender,
                    label: t(`profile.creation.genderOptions.${gender.toLowerCase()}`)
                  }))}
                  onChange={(e) => field.handleChange(e.target.value as GenderType)}
                  error={field.state.meta.errors[0] || undefined}
                />
              )}
            </form.Field>

            <Separator />

            {/* Referrer Code Field */}
            <form.Field
              name="referrerCode"
              validators={{
                onChange: ({}) => undefined // value?.trim() ? undefined : t("profile.creation.errors.referrerCodeRequired")
              }}
            >
              {(field) => (
                <div>
                  <Input
                    id="name"
                    label={t("profile.creation.referrerCode")}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t("profile.creation.referrerCodePlaceholder")}
                    disabled={form.state.isSubmitting}
                    error={field.state.meta.errors[0] || undefined}
                  />
                  <p className="text-text-secondary text-responsive-sm mt-0.5">
                    {t("profile.creation.referrerCodeNote")}
                  </p>
                </div>
              )}
            </form.Field>

            {/* Submit Button */}
            <Button isDisabled={form.state.isSubmitting} className="w-full text-responsive-base">
              {form.state.isSubmitting ? (
                <div className="e-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t("profile.creation.creating")}</span>
                </div>
              ) : (
                t("profile.creation.submit")
              )}
            </Button>
          </form>

          {/* Sign in with another account button */}
          <div className="mt-2 text-center">
            <button
              onClick={handleSignInAnotherAccount}
              disabled={form.state.isSubmitting}
              className="text-responsive-base text-text-secondary hover:text-text-primary transition-colors duration-200 underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("profile.creation.signInAnotherAccount")}
            </button>
          </div>

          {/* <div className="mt-6 text-center">
            <p className="text-responsive-base text-text-secondary">{t("profile.creation.freeCredits")}</p>
          </div> */}
        </div>
      </div>

      {/* Credits Popup */}
      {showCreditsPopup && (
        <div className="fixed inset-0 backdrop-blur-sm e-center z-50 p-4">
          <div className="bg-background-secondary rounded-sm p-8 max-w-sm w-full text-center border border-border-dark">
            <h2 className="text-responsive-2xl font-normal text-text-primary mb-2">
              {t("profile.creation.welcome")}
            </h2>
            <p className="text-text-secondary mb-4 body-base">{t("profile.creation.welcomeMessage")}</p>
            <div className="bg-background-highlight rounded-sm p-4 mb-4">
              <p className="text-text-primary font-semibold">🎉 {t("profile.creation.creditsAdded")}</p>
              <p className="text-text-secondary text-sm">{t("profile.creation.creditsMessage")}</p>
            </div>
            <Button
              onClick={() => {
                setShowCreditsPopup(false);
                onComplete();
              }}
            >
              {t("profile.creation.getStarted")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
